import { format, parseISO } from "date-fns";

import { DATE_PRESETS, toLocalYmd, TYPE_FILTER_OPTIONS } from "./constants";

import type { DatePreset } from "./constants";

export const parseFilterDate = (raw: string | undefined): string | undefined =>
  raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined;

export const hasFutureDate = (...dates: (string | undefined)[]): boolean => {
  const today = toLocalYmd(new Date());
  return dates.some((d) => d !== undefined && d > today);
};

export const TYPE_VALID_VALUES: string[] = TYPE_FILTER_OPTIONS.map(
  (o) => o.value
);

/**
 * Find the preset whose date range matches the given start/end values.
 * Returns `null` when no preset matches (i.e. a custom range).
 *
 * By default compares against the live `minDate()`/`maxDate()` on each
 * preset. Pass `getRange` to compare against pre-computed (frozen) values
 * instead — see `DateRangeFilterPanel` for why this matters at midnight.
 */
export const findMatchingPreset = <T extends DatePreset>(
  from: string | undefined,
  to: string | undefined,
  presets: T[] = DATE_PRESETS as T[],
  getRange: (p: T) => { min: string; max: string } = (p) => ({
    min: p.minDate(),
    max: p.maxDate(),
  })
): T | null => {
  if (!from || !to) return null;
  return (
    presets.find((p) => {
      const range = getRange(p);
      return range.min === from && range.max === to;
    }) ?? null
  );
};

/**
 * Format a YYYY-MM-DD date range for display.
 *
 * Same year:      "May 13 - Jun 13, 2026"
 * Different year: "May 13, 2025 - Jun 13, 2026"
 */
export const formatDateRange = (from: string, to: string): string => {
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  const sameYear = fromDate.getFullYear() === toDate.getFullYear();

  const fromStr = format(fromDate, sameYear ? "MMM d" : "MMM d, yyyy");
  const toStr = format(toDate, "MMM d, yyyy");

  return `${fromStr} - ${toStr}`;
};

/**
 * Derive the summary caption text based on filter state.
 *
 * - No filters active → "Last 30 days" (the default window)
 * - Filters active, no date range → "All time · Filtered"
 * - Date matches a preset → preset label (e.g. "Last 90 days")
 * - Custom date range → formatted range (e.g. "May 13 - Jun 13, 2026")
 */
export const buildSummaryCaption = (
  isFiltered: boolean,
  settledFrom: string | undefined,
  settledTo: string | undefined,
  getString: (id: string) => string
): string => {
  if (!isFiltered) {
    return getString("live-ledger-summary-caption-default");
  }

  if (settledFrom && settledTo) {
    const preset = findMatchingPreset(settledFrom, settledTo);
    if (preset) {
      return getString(preset.ftlId);
    }
    return formatDateRange(settledFrom, settledTo);
  }

  return getString("live-ledger-summary-caption-filtered");
};
